/**
 * Chem-doku engine.
 *
 * Sudoku played with chemistry symbols instead of digits. Each row,
 * column, and box must contain every symbol in the active "pack"
 * exactly once. Two sizes:
 *
 *   - 4x4 grid, 2x2 boxes, 4 symbols (beginner)
 *   - 6x6 grid, 2 rows x 3 cols boxes (boxRows=2, boxCols=3), 6 symbols
 *
 * Generation strategy: build a canonical Latin square that already
 * satisfies the box constraint, then apply random structure-preserving
 * permutations (swap rows within a band, columns within a stack, swap
 * whole bands/stacks, relabel symbols). The result is a valid sudoku
 * with a different look every time. We then hide cells uniformly at
 * random per difficulty - we don't enforce uniqueness because at 4x4
 * and 6x6 with the clue counts we use, ambiguity is rare and the puzzle
 * is forgiving (players can clear and retry).
 *
 * Deterministic mode: pass a `seed` to get a repeatable puzzle - useful
 * for "puzzle of the day" style hooks later, and for tests.
 */

export type ChemDokuSize = 4 | 6;

export interface ChemDokuPack {
  /** Stable id used in URLs / storage. */
  id: string;
  label: string;
  /** One-line description shown on the pack picker. */
  description: string;
  /** Exactly `size` symbols. */
  symbols: string[];
}

export type Difficulty = "easy" | "medium" | "hard";

export interface ChemDokuPuzzle {
  size: ChemDokuSize;
  boxRows: number;
  boxCols: number;
  symbols: string[];
  /** Solved board, indexed `[row][col]` -> symbol. */
  solution: string[][];
  /** Starting board; empty cells are `null`. */
  given: (string | null)[][];
  /** Cells locked as clues - true means "do not edit". */
  locked: boolean[][];
}

// ---------- PRNG ----------

function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(arr: T[], rand: () => number): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ---------- canonical Latin square ----------

/**
 * Build a canonical sudoku-valid grid for the given size where the cell
 * `[r][c]` equals `(boxCols * (r % boxRows) + Math.floor(r / boxRows) +
 * c) % size`. The classic sudoku construction.
 */
function canonicalGrid(size: ChemDokuSize, boxRows: number, boxCols: number): number[][] {
  const grid: number[][] = [];
  for (let r = 0; r < size; r++) {
    const row: number[] = [];
    for (let c = 0; c < size; c++) {
      row.push((boxCols * (r % boxRows) + Math.floor(r / boxRows) + c) % size);
    }
    grid.push(row);
  }
  return grid;
}

// ---------- structure-preserving permutations ----------

function permuteRowsWithinBands(
  grid: number[][],
  boxRows: number,
  rand: () => number,
): number[][] {
  const size = grid.length;
  const out: number[][] = new Array(size);
  for (let band = 0; band < size; band += boxRows) {
    const order = shuffle(
      Array.from({ length: boxRows }, (_, i) => band + i),
      rand,
    );
    for (let i = 0; i < boxRows; i++) out[band + i] = grid[order[i]];
  }
  return out;
}

function permuteColsWithinStacks(
  grid: number[][],
  boxCols: number,
  rand: () => number,
): number[][] {
  const size = grid.length;
  const out = grid.map((row) => row.slice());
  for (let stack = 0; stack < size; stack += boxCols) {
    const order = shuffle(
      Array.from({ length: boxCols }, (_, i) => stack + i),
      rand,
    );
    for (let r = 0; r < size; r++) {
      for (let i = 0; i < boxCols; i++) out[r][stack + i] = grid[r][order[i]];
    }
  }
  return out;
}

function permuteBands(
  grid: number[][],
  boxRows: number,
  rand: () => number,
): number[][] {
  const size = grid.length;
  const bandCount = size / boxRows;
  const order = shuffle(
    Array.from({ length: bandCount }, (_, i) => i),
    rand,
  );
  const out: number[][] = new Array(size);
  for (let i = 0; i < bandCount; i++) {
    for (let r = 0; r < boxRows; r++) {
      out[i * boxRows + r] = grid[order[i] * boxRows + r];
    }
  }
  return out;
}

function permuteStacks(
  grid: number[][],
  boxCols: number,
  rand: () => number,
): number[][] {
  const size = grid.length;
  const stackCount = size / boxCols;
  const order = shuffle(
    Array.from({ length: stackCount }, (_, i) => i),
    rand,
  );
  return grid.map((row) => {
    const next: number[] = new Array(size);
    for (let i = 0; i < stackCount; i++) {
      for (let c = 0; c < boxCols; c++) {
        next[i * boxCols + c] = row[order[i] * boxCols + c];
      }
    }
    return next;
  });
}

function relabel(grid: number[][], rand: () => number): number[][] {
  const size = grid.length;
  const map = shuffle(
    Array.from({ length: size }, (_, i) => i),
    rand,
  );
  return grid.map((row) => row.map((v) => map[v]));
}

// ---------- difficulty tuning ----------

/**
 * Number of clues to leave visible. Lower = harder. Tuned so that easy
 * is gentle for a first-timer and hard still feels solvable in a few
 * minutes without needing a uniqueness solver.
 */
function clueCount(size: ChemDokuSize, difficulty: Difficulty): number {
  if (size === 4) {
    return { easy: 10, medium: 8, hard: 6 }[difficulty];
  }
  // 6x6
  return { easy: 22, medium: 18, hard: 14 }[difficulty];
}

// ---------- public API ----------

export interface GenerateOptions {
  size: ChemDokuSize;
  difficulty: Difficulty;
  pack: ChemDokuPack;
  /** Optional seed for deterministic generation. */
  seed?: string;
}

export function generatePuzzle(opts: GenerateOptions): ChemDokuPuzzle {
  const { size, difficulty, pack } = opts;
  if (pack.symbols.length !== size) {
    throw new Error(
      `Pack "${pack.id}" has ${pack.symbols.length} symbols, expected ${size}.`,
    );
  }

  // 4x4 -> 2x2 boxes; 6x6 -> 2 rows x 3 cols boxes (standard variant).
  const boxRows = size === 4 ? 2 : 2;
  const boxCols = size === 4 ? 2 : 3;

  const seedStr =
    opts.seed ?? `chemdoku:${pack.id}:${size}:${difficulty}:${Date.now()}:${Math.random()}`;
  const rand = mulberry32(hashString(seedStr));

  let grid = canonicalGrid(size, boxRows, boxCols);
  grid = permuteRowsWithinBands(grid, boxRows, rand);
  grid = permuteColsWithinStacks(grid, boxCols, rand);
  grid = permuteBands(grid, boxRows, rand);
  grid = permuteStacks(grid, boxCols, rand);
  grid = relabel(grid, rand);

  const solution: string[][] = grid.map((row) => row.map((v) => pack.symbols[v]));

  // Decide which cells stay visible.
  const total = size * size;
  const visible = clueCount(size, difficulty);
  const indices = shuffle(
    Array.from({ length: total }, (_, i) => i),
    rand,
  );
  const showSet = new Set(indices.slice(0, visible));

  const given: (string | null)[][] = [];
  const locked: boolean[][] = [];
  for (let r = 0; r < size; r++) {
    const gRow: (string | null)[] = [];
    const lRow: boolean[] = [];
    for (let c = 0; c < size; c++) {
      const idx = r * size + c;
      const show = showSet.has(idx);
      gRow.push(show ? solution[r][c] : null);
      lRow.push(show);
    }
    given.push(gRow);
    locked.push(lRow);
  }

  return { size, boxRows, boxCols, symbols: pack.symbols.slice(), solution, given, locked };
}

/** True if every cell is filled and matches the solution. */
export function isSolved(
  board: (string | null)[][],
  solution: string[][],
): boolean {
  for (let r = 0; r < board.length; r++) {
    for (let c = 0; c < board[r].length; c++) {
      if (board[r][c] !== solution[r][c]) return false;
    }
  }
  return true;
}

/**
 * Find every cell that conflicts with another cell in the same row,
 * column, or box. Empty cells never conflict with anything (so the
 * grid doesn't light up red while you're still filling it in).
 */
export function findConflicts(
  board: (string | null)[][],
  boxRows: number,
  boxCols: number,
): boolean[][] {
  const size = board.length;
  const out: boolean[][] = Array.from({ length: size }, () =>
    new Array<boolean>(size).fill(false),
  );

  const mark = (cells: Array<[number, number]>) => {
    const seen = new Map<string, Array<[number, number]>>();
    for (const [r, c] of cells) {
      const v = board[r][c];
      if (!v) continue;
      const list = seen.get(v) ?? [];
      list.push([r, c]);
      seen.set(v, list);
    }
    for (const list of seen.values()) {
      if (list.length > 1) {
        for (const [r, c] of list) out[r][c] = true;
      }
    }
  };

  for (let r = 0; r < size; r++) {
    mark(Array.from({ length: size }, (_, c) => [r, c] as [number, number]));
  }
  for (let c = 0; c < size; c++) {
    mark(Array.from({ length: size }, (_, r) => [r, c] as [number, number]));
  }
  for (let br = 0; br < size; br += boxRows) {
    for (let bc = 0; bc < size; bc += boxCols) {
      const cells: Array<[number, number]> = [];
      for (let r = br; r < br + boxRows; r++) {
        for (let c = bc; c < bc + boxCols; c++) {
          cells.push([r, c]);
        }
      }
      mark(cells);
    }
  }
  return out;
}